import React, { useEffect, useState } from 'react';
import api from '../../core/api';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import CustomerPicker from '../factors/CustomerPicker';
import { parseNum, formatNumber } from '../../utils/factorHelpers';

export const METHOD_LABELS = {
  card: 'کارت‌خوان',
  account: 'به حساب',
  cheque: 'چک',
  charity: 'امور خیریه',
  misc: 'متفرقه',
  other: 'غیره',
};

export const NO_CUSTOMER_METHODS = ['charity', 'misc'];

export default function TransactionForm({ form, setForm, customer, setCustomer, onDirty }) {
  const set = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    onDirty?.();
  };

  const [banks, setBanks] = useState([]);
  const [banksLoading, setBanksLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setBanksLoading(true);
    api
      .get('/banks')
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.banks || [];
        setBanks(list);
        setForm((prev) => {
          if (prev.bankId) return prev;
          const def = list.find((b) => b.isDefault) || list[0];
          return def ? { ...prev, bankId: String(def.id) } : prev;
        });
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setBanksLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isCharity = form.method === 'charity';
  const noCustomer = NO_CUSTOMER_METHODS.includes(form.method);
  const descRequired = form.method === 'other' || form.method === 'misc';

  return (
    <>
      <div className="card">
        <h2>اطلاعات تراکنش</h2>
        <div className="grid">
          <div>
            <label>نوع *</label>
            <select
              value={form.direction}
              onChange={(e) => set({ direction: e.target.value })}
            >
              <option value="receive">دریافت</option>
              <option value="pay">پرداخت</option>
            </select>
          </div>

          <div>
            <label>روش *</label>
            <select
              value={form.method}
              onChange={(e) => {
                const method = e.target.value;
                if (NO_CUSTOMER_METHODS.includes(method)) setCustomer(null);
                set({ method });
              }}
            >
              {Object.entries(METHOD_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label>تاریخ</label>
            <JalaliDatePicker
              value={form.date}
              onChange={(v) => set({ date: v })}
              placeholder="انتخاب تاریخ"
              name="date"
            />
          </div>

          <div>
            <label>مبلغ (ریال) *</label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              required
              value={form.amount ? formatNumber(parseNum(form.amount)) : ''}
              onChange={(e) => {
                const raw = parseNum(e.target.value);
                set({ amount: raw ? String(raw) : '' });
              }}
              placeholder="مثلاً 5,000,000"
            />
          </div>

          <div className="field-full">
            <label>حساب بانکی</label>

            {banksLoading ? (
              <>
                <style>{`
                  @keyframes tf-skeleton {
                    0% { background-position: 200% 0; }
                    100% { background-position: -200% 0; }
                  }
                `}</style>
                <div
                  aria-hidden="true"
                  style={{
                    height: 42,
                    width: '100%',
                    borderRadius: 10,
                    background:
                      'linear-gradient(90deg, var(--border) 25%, var(--border-strong) 50%, var(--border) 75%)',
                    backgroundSize: '200% 100%',
                    animation: 'tf-skeleton 1.4s ease-in-out infinite',
                  }}
                />
              </>
            ) : (
              <>
                <select
                  value={form.bankId || ''}
                  onChange={(e) => set({ bankId: e.target.value })}
                >
                  {banks.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.bankName} — {b.holderName}
                      {b.isDefault ? ' (پیش‌فرض)' : ''}
                    </option>
                  ))}
                  {/* حسابی که حذف شده ولی هنوز روی این تراکنش ثبت است */}
                  {form.bankId &&
                    form.bankId !== 'none' &&
                    !banks.some((b) => String(b.id) === String(form.bankId)) && (
                      <option value={form.bankId}>حساب حذف‌شده</option>
                    )}
                </select>
                {banks.length === 0 && (
                  <div className="muted" style={{ marginTop: 6 }}>
                    هنوز حساب بانکی ثبت نشده است؛ از بخش تنظیمات اضافه کنید.
                  </div>
                )}
              </>
            )}
          </div>

          {(form.method === 'card' || form.method === 'account') && (
            <div className="field-full">
              <label>شماره پیگیری *</label>
              <input
                required
                autoComplete="off"
                value={form.trackingNo}
                onChange={(e) => set({ trackingNo: e.target.value })}
              />
            </div>
          )}

          {form.method === 'cheque' && (
            <div className="field-full">
              <label>سریال چک *</label>
              <input
                required
                autoComplete="off"
                value={form.chequeSerial}
                onChange={(e) => set({ chequeSerial: e.target.value })}
              />
            </div>
          )}

          {isCharity && (
            <div className="field-full">
              <label>نام خیریه *</label>
              <input
                required
                autoComplete="off"
                value={form.charityName}
                onChange={(e) => set({ charityName: e.target.value })}
              />
            </div>
          )}

          <div className="field-full">
            <label>توضیحات {descRequired ? '*' : '(اختیاری)'}</label>
            <input
              required={descRequired}
              autoComplete="off"
              value={form.description}
              onChange={(e) => set({ description: e.target.value })}
            />
          </div>
        </div>
      </div>

      {!noCustomer && (
        <div className="card">
          <h2>مشتری *</h2>
          <CustomerPicker
            customer={customer}
            onSelect={(c) => {
              setCustomer(c);
              onDirty?.();
            }}
          />
        </div>
      )}
    </>
  );
}